/**
 * Feature-flag labels and descriptions come from config/feature_registry.json,
 * which is the single source of truth for the flags themselves and stays
 * language-neutral. The human-readable half of each entry is translated in
 * translation.json under `featureFlags`, keyed by the flag id.
 *
 * The registry value is the fallback, so a flag added without a translation
 * still renders its English label instead of a raw key.
 *
 * These are looked up against the resource bundle rather than through `t()`
 * because a flag id is hostile to i18next's key grammar: "." is the key
 * separator and "_" is the plural/context separator, so "modules.agents" and
 * every escaping of it ("modules__agents") gets re-parsed into a path that
 * does not exist. Reading the bundle sidesteps the grammar entirely.
 *
 * Callers pass the current language, which they already hold from
 * useLanguage — that is also what makes them re-render when it changes.
 */
import i18n from '@/i18n';

type FeatureText = { label?: string; description?: string };

function entry(lang: string, flagKey: string): FeatureText | undefined {
  const bundle = i18n.getResourceBundle?.(lang, 'translation') as
    | { featureFlags?: Record<string, unknown> }
    | undefined;
  // The bundle is a TREE, so `featureFlags['modules.agents']` finds nothing —
  // there is no property spelled with a dot in it. Walk the id as a path, the
  // way i18next would have, minus the plural/context parsing that made the
  // flag ids unusable through `t()` in the first place.
  let node: unknown = bundle?.featureFlags;
  for (const part of flagKey.split('.')) {
    if (!node || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node && typeof node === 'object' ? (node as FeatureText) : undefined;
}

export function featureLabel(lang: string, flagKey: string, fallback?: string): string {
  return entry(lang, flagKey)?.label || fallback || flagKey;
}

export function featureDescription(lang: string, flagKey: string, fallback?: string): string {
  return entry(lang, flagKey)?.description || fallback || '';
}
